import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { contractService } from "@/services/contractService";
import type {
  Contract,
  ContractCreatePayload,
  ContractListParams,
  ContractSignaturePayload,
  ContractUpdatePayload,
  IndicacaoCreatePayload,
  IndicacaoUpdatePayload,
} from "@/types/contract";

const keys = {
  list: (wsId: number, params?: ContractListParams) =>
    ["contracts", wsId, params ?? "all"] as const,
  one: (wsId: number, id: number) => ["contracts", wsId, "one", id] as const,
};

export function useContracts(workspaceId: number | null, params?: ContractListParams) {
  return useQuery({
    queryKey: keys.list(workspaceId ?? 0, params),
    queryFn: () => contractService.list(workspaceId as number, params),
    enabled: workspaceId != null,
  });
}

/** FE-010: variante que preserva `meta` (total real p/ KPIs + aviso de truncamento). */
export function useContractsPaged(workspaceId: number | null, params?: ContractListParams) {
  return useQuery({
    queryKey: [...keys.list(workspaceId ?? 0, params), "paged"] as const,
    queryFn: () => contractService.listPaged(workspaceId as number, params),
    enabled: workspaceId != null,
  });
}

export function useContract(workspaceId: number | null, id: number | null) {
  return useQuery({
    queryKey: keys.one(workspaceId ?? 0, id ?? 0),
    queryFn: () => contractService.get(workspaceId as number, id as number),
    enabled: workspaceId != null && id != null,
  });
}

export function useCreateContract(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractCreatePayload) =>
      contractService.create(workspaceId, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contracts", workspaceId] }),
  });
}

export function useUpdateContract(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractUpdatePayload) =>
      contractService.update(workspaceId, id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contracts", workspaceId] });
      void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) });
    },
  });
}

export function useDeleteContract(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => contractService.remove(workspaceId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contracts", workspaceId] }),
  });
}

export function useTransitionContract(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (to: Contract["status"]) => contractService.transition(workspaceId, id, to),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contracts", workspaceId] });
      void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) });
    },
  });
}

export function useAttachSignature(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractSignaturePayload) =>
      contractService.attachSignature(workspaceId, id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contracts", workspaceId] });
      void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) });
    },
  });
}

// ─── Teses links ─────────────────────────────────────────────────────────

export function useAddContractTese(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teseId: number) => contractService.addTese(workspaceId, id, teseId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) }),
  });
}

export function useRemoveContractTese(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => contractService.removeTese(workspaceId, id, linkId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) }),
  });
}

// ─── Indicações ────────────────────────────────────────────────────────────

export function useAddIndicacao(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: IndicacaoCreatePayload) =>
      contractService.addIndicacao(workspaceId, id, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) }),
  });
}

export function useUpdateIndicacao(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ indId, payload }: { indId: number; payload: IndicacaoUpdatePayload }) =>
      contractService.updateIndicacao(workspaceId, id, indId, payload),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) }),
  });
}

export function useRemoveIndicacao(workspaceId: number, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (indId: number) => contractService.removeIndicacao(workspaceId, id, indId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.one(workspaceId, id) }),
  });
}
